import React, { useEffect, useState, FormEvent } from 'react';
import { useParams, useNavigate } from 'react-router';
import { FileText, Search, ChevronRight, BoltIcon } from 'lucide-react';
import BottomNav from '../../components/BottomNav.tsx';
import { VehicleModel } from '../../Interfaces/vehicleModle.ts';
import { Building } from '../../Interfaces/building.ts';
import { WorkStation } from '../../Interfaces/workstation.ts';
import ClientBottomNav from '../../components/ClientBottomNav.tsx';
import { baseurly } from '../../utils/apis.ts';
import { ProcessDoc } from '../../Interfaces/processDocs.ts';
import { DesignDoc } from '../../Interfaces/designDocs.ts';
import { X_API_KEY } from '../../utils/apis.ts';
import { baseurl } from '../../utils/apis.ts';
import {useAuth} from "../../context/authContext.tsx";
import MainLayout from '../../layouts/MainLayout.tsx';

interface RouteParams {
  vehicleId: string;
}

const DynamicWorkstationPage012: React.FC = () => {
  const {employeeI, authReady} = useAuth()
  const { vehicleId } = useParams();
  const navigate = useNavigate();

  // State for vehicle details.
  const [vehicle, setVehicle] = useState<VehicleModel | null>(null);
  const [loadingVehicle, setLoadingVehicle] = useState<boolean>(true);

  // State for buildings.
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [loadingBuildings, setLoadingBuildings] = useState<boolean>(true);

  // Error state.
  const [error, setError] = useState<string | null>(null);

  const [processDoc, setProcessDoc] = useState<ProcessDoc | undefined>(undefined);
  const [designDoc, setDesignDoc] = useState<DesignDoc | undefined>(undefined);
  const [showAddProcessModal, setShowAddProcessModal] = useState<boolean>(false);
  const [buildProcessName, setBuildProcessName] = useState<string>('');
  const [buildProcessDescription, setBuildProcessDescription] = useState<string>('');
  const [buildProcessFile, setBuildProcessFile] = useState<File | null>(null);
  const [addingProcess, setAddingProcess] = useState<boolean>(false);
  const [addProcessError, setAddProcessError] = useState<string | null>(null);

  useEffect(() => {
    if (!authReady) {
      return;
    }
    if (!employeeI) {
      navigate('/login', {replace: true});
      return;
    }
    if (vehicleId) {
      fetch(`${baseurly}/vehicle_model/${vehicleId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': X_API_KEY,
              
        },
          credentials: "include",
        }
      )
        .then((res) => {
          if (!res.ok) {
            throw new Error('Failed to fetch vehicle model');
          }
          return res.json();
        })
        .then((data) => {
          setVehicle(data);
          setLoadingVehicle(false);
          setProcessDoc(data.process_doc);
          setDesignDoc(data.design_doc)
        })
        .catch((err) => {
          console.error('Error fetching vehicle model:', err);
          setError('Unable to load vehicle model');
          setLoadingVehicle(false);
        });
        
      fetch(`${baseurly}/buildings`,{
              
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'X-API-KEY': X_API_KEY,
            },
            credentials: "include",
          })
        .then((res) => {
          if (!res.ok) {
            throw new Error('Failed to fetch buildings');
          }
          return res.json();
        })
        .then((data) => {
          setBuildings(data);
          setLoadingBuildings(false);
        })
        .catch((err) => {
          console.error('Error fetching buildings:', err);
          setError('Unable to load buildings');
          setLoadingBuildings(false);
        });
    };
  }, [authReady, employeeI, navigate, vehicleId]);

  const handleBuildingView = (buildingId: number) => {
    fetch(`${baseurly}/buildings/${buildingId}/work_stations`,{
            
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-API-KEY': X_API_KEY,
          },
          credentials: "include",
        })
      .then((res) => {
        if (!res.ok) {
          throw new Error('Failed to fetch work station');
        }
        return res.json();
      })
      .then((workStation: WorkStation) => {
        const workstationId = workStation.id; 
        if (vehicle) {
          navigate(`/client/vehicle/${vehicle.id}/building/${buildingId}`);
        }
      })
      .catch((err) => {
        console.error('Error fetching work station:', err);
        alert('Unable to fetch work station for this building.');
      });
  };

  const handleAddBuildProcess = async (e: FormEvent) => {
    e.preventDefault();
    if (!buildProcessFile) {
      setAddProcessError('Please select a file.');
      return;
    }
    if (!buildProcessName.trim()) {
      setAddProcessError('Please enter a title.');
      return;
    }
    setAddProcessError(null);
    setAddingProcess(true);

    try {
      const formData = new FormData();
      formData.append('file', buildProcessFile);

      const uploadResponse = await fetch(`${baseurly}/upload/buildingProcess`, {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error('File upload failed');
      }

      const uploadData = await uploadResponse.json();
      const payload = {
        name: buildProcessName,
        description: buildProcessDescription,
        file: uploadData,
        vehicle_model_id: vehicle ? vehicle.id : null,
      };

      const buildProcessResponse = await fetch(`${baseurly}/build_process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload),
      });

      if (!buildProcessResponse.ok) {
        throw new Error('Failed to create build process');
      }

      setBuildProcessName('');
      setBuildProcessDescription('');
      setBuildProcessFile(null);
      setShowAddProcessModal(false);
      alert('Build process added successfully!');
    } catch (err) {
      console.error(err);
      setAddProcessError((err as Error).message);
    } finally {
      setAddingProcess(false);
    }
  };

  return (
    <MainLayout>
    <div className="min-h-screen bg-blue-900 pb-20">
      

      {/* Page Title */}
      <div className="p-4 sm:p-6">
        <h1 className="text-white  text-lg font-medium">
          {loadingVehicle ? 'Loading...' : vehicle ? vehicle.model_name : 'Unknown Vehicle'} Details
        </h1>
      </div>

      {/* Search Bar */}
      <div className="px-4 sm:px-6 mb-4 sm:mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search for instructions..."
            className="w-full py-3 px-4 pr-10 rounded-full"
          />
          <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
        </div>
      </div>

      {/* Vehicle Details Card */}
      <div className="bg-white ring-2 ring-white-500 p-4 rounded-t-3xl">
        <div className="bg-white border rounded-3xl p-4 sm:p-8 mx-2 sm:mx-6 mb-4 sm:mb-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="w-full md:w-1/2 mb-4 md:mb-0">
              <h2 className="text-blue-900 text-xl font-bold mb-2">Vehicle Details</h2>
              <p className="text-gray-600">Model: {vehicle ? vehicle.model_name : 'Not available'}</p>
              <p className="text-gray-600">Type: {vehicle ? vehicle.vehicle_type : 'N/A'}</p>
              
              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  onClick={() => navigate(`/vehicle/${vehicleId}/build-process-client`)}
                  className="bg-blue-900 text-white px-3 py-2 rounded-lg hover:bg-blue-800 flex items-center gap-2 text-sm sm:text-base"
                >
                  <FileText size={16} />
                  Build Process
                </button>
                
                <button
                  onClick={() => navigate(`/process-document/${processDoc?.id}`)}
                  className="bg-blue-900 text-white px-3 py-2 rounded-lg hover:bg-blue-800 flex items-center gap-2 text-sm sm:text-base"
                >
                  <FileText size={16} />
                  Process Doc
                </button>

                <button
                  onClick={() => navigate(`/designSpec-document/${designDoc?.id}`)}
                  className="bg-blue-900 text-white px-3 py-2 rounded-lg hover:bg-blue-800 flex items-center gap-2 text-sm sm:text-base"
                >
                  <BoltIcon size={16} />
                  Tech Specs
                </button>
              </div>
            </div>
            
            <div className="w-full md:w-1/2 flex justify-center mt-4 md:mt-0">
              <img
                src={`${baseurl}files/${vehicle?.image}`}
                alt={vehicle ? vehicle.model_name : 'Placeholder'}
                className="w-40 h-40 sm:w-60 sm:h-60 object-contain"
              />
            </div>
          </div>
        </div>

        {/* Buildings Section */}
        <div className="bg-white rounded-3xl p-4 sm:p-6 mx-2 sm:mx-6">
          <h2 className="text-blue-900 text-xl font-bold mb-4">Production Lines</h2>
          {loadingBuildings ? (
            <p>Loading Workshops...</p>
          ) : buildings.length > 0 ? (
            <ul className="space-y-3">
              {buildings
              .sort((a, b) => a.id - b.id)
              .map((building) => (
                <li
                  key={building.id}
                  onClick={() => handleBuildingView(building.id)}
                  className="p-4 border bg-sky-200 font-semibold rounded-lg flex justify-between items-center hover:bg-blue-200 cursor-pointer"
                >
                  {building.building_name}
                  <ChevronRight className="text-blue-900" />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600">No Workshops available.</p>
          )}
        </div>
      </div>

      {/* Modal for Adding Build Process */}
      {showAddProcessModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 w-11/12 max-w-md">
            <h2 className="text-xl font-bold mb-4">Add Build Process</h2>
            {addProcessError && <p className="text-red-500 mb-2">{addProcessError}</p>}
            <form onSubmit={handleAddBuildProcess}>
              <div className="mb-4">
                <label className="block text-gray-700">Title</label>
                <input
                  type="text"
                  value={buildProcessName}
                  onChange={(e) => setBuildProcessName(e.target.value)}
                  className="w-full border px-3 py-2 rounded"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">Description</label>
                <textarea
                  value={buildProcessDescription}
                  onChange={(e) => setBuildProcessDescription(e.target.value)}
                  className="w-full border px-3 py-2 rounded"
                  rows={3}
                ></textarea>
              </div>
              <div className="mb-4">
                <label className="block text-gray-700">File (PDF)</label>
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setBuildProcessFile(e.target.files[0]);
                    }
                  }}
                  className="w-full"
                  required
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddProcessModal(false)}
                  className="px-4 py-2 rounded bg-gray-300 hover:bg-gray-400"
                  disabled={addingProcess}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700"
                  disabled={addingProcess}
                >
                  {addingProcess ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <ClientBottomNav showBack={true} />
    </div>
    </MainLayout>
  );
};

export default DynamicWorkstationPage012;
